<script lang="ts">
	import type { Store } from '$lib/def/landing';
	import type { stores$ as storesStream } from '$lib/serv/groc-landing';
	import type { Address } from 'proto/address';

	import { browser } from '$app/environment';
	import { Vertical } from '$lib/def/verticals';
	import { profile$ } from '$lib/serv/core/auth';
	import { LOCATION_PROPS, addLocation, dissectGooglePlaceAddress, geoOpted$, locations$, removeCountryAndPostalCode, setGeoOpted, streamCurrentAddresses, type Location } from '$lib/serv/locations';
	import { showSignInModal, showStoreSelectModal } from "$lib/serv/nav";
	import { getAddressLabel, xaddress } from "$lib/util/address";
	import { parseObjectValuesToString } from '$lib/util/format';
	import { RxWritable } from '$lib/util/obs';
	import { query, queryFrom } from '$lib/util/url';
	import { EMPTY, Observable, Subscription, debounceTime, distinctUntilChanged, map, tap } from 'rxjs';
	import { createEventDispatcher, onDestroy, onMount } from 'svelte';

	import Search from '$lib/ui/form/Search.svelte';
	import StoreOption from '$lib/ui/layout/StoreOption.svelte';
	import Illustration from './Illustration.svelte';

	export let title = '';
	export let navOnSelect = true;
	export let currentURL: URL | undefined = undefined;
	export let selectedLabel = '';
	export let stores$: typeof storesStream | undefined = undefined;
	export let additionalParams: [string, any][] = [];

	/**
	 * If true, the `goto` will not go to the home route, but instead add the location
	 * params to the current URL.
	 */
	export let onlyParams = false;

	$: xp = additionalParams.map(([k,v]) => `${k}=${v}`).join('&');
	$: vertical = currentURL?.searchParams.get('vertical') ?? undefined;

	let addr = new RxWritable<string>('');
	let gmBusy = false;
	let searchIsActive = false;
	let geolocationAddresses$ = browser ? streamCurrentAddresses() : EMPTY;

	let choosePlace: (placeId:string) => Promise<void>;
	let results: Observable<google.maps.places.AutocompletePrediction[] | null>;
	let subStores: Subscription | undefined;
	let loadingStores = true;
	let storesError = '';
	let stores: Store[];
	let dispatch = createEventDispatcher<{selected:{type:'place'|'saved'|'recent'|'geo'|'store', location:Location}}>();
	let dispatcher = createEventDispatcher();

  const qryParams = Array.from(currentURL?.searchParams.entries() ?? [])
    .filter(([k, v]) => !LOCATION_PROPS.includes(k as keyof Address));

	const savedAddresses$ = profile$.pipe(
		map(user => (user?.saved_addresses?.length && user?.saved_addresses) || null),
		map((list) => list?.map((addr: Address) => {
			const obj = parseObjectValuesToString(addr);
			let query = queryFrom(obj).setBulk(qryParams);
			query.remove('saved_delivery_instruction');
			if (vertical) query.set('vertical', vertical);
			return {
				...addr,
				query: query.toString(),
			}
		}))
	);
	const recent$ = locations$.pipe(
		map((list) => list?.map(loc => {
			const query = queryFrom(parseObjectValuesToString(loc)).setBulk(qryParams);
			if (vertical) query.set('vertical', vertical);
			return {
				...loc,
				query: query.toString(),
			}
		}))
	);

	let showLogin = false;

	function toggleSignIn() {
		dispatcher('signin', true);
		showLogin = false;
		showSignInModal.update(() => true);
	}

	let isSearchReady = false;
	async function loadMaps() {
		if (!browser) return; // not really needed because this method is a click handler

		// dynamically import client-side-only libraries
		const { load, predictor, getSessionToken, promisePlaceDetails } = await import('$lib/serv/vendor-integration/google-maps');

		// load maps SDK
		try {
			await load();
		} catch (error) {
			console.error(error);
			// return;
		}

		const token = await getSessionToken();

		// stream: search string -> place predictions -> results UI
		results = addr.pipe(
			debounceTime(300),
			distinctUntilChanged(),
			tap(() => gmBusy = true),
			predictor(token),
			tap(() => gmBusy = false),
			map(res => res?.predictions ?? null),
		)

		// on selecting a place prediction, get its details
		choosePlace = async (placeId) => {
			const place = await promisePlaceDetails(placeId, token);
			let location: Location = {
				place_id: place.place_id,
				latitude: place.geometry?.location?.lat().toString() ?? '',
				longitude: place.geometry?.location?.lng().toString() ?? '',
				...dissectGooglePlaceAddress(place.address_components ?? []),
			};

			// store the location for convenience
			addLocation(location);

			if (navOnSelect) {
				queryFrom(parseObjectValuesToString(location))
				.setBulk(additionalParams)
				.set('vertical', vertical ?? 'restaurant')
				.go(onlyParams ? '' : '/');
			} else {
				dispatch('selected', { type: 'place', location })
			}
		}

		setTimeout(() => isSearchReady = true, 1000); // Google maps is not instantly ready
	}

	onMount(() => {
		loadMaps();

		subStores = stores$?.subscribe(cache => {
			loadingStores = !!cache.loading;
			storesError = cache.error ?? '';
			if (cache.payload) {
				stores = cache.payload;
				// dispatch('store', stores.find(s => s.default)?.id ?? '')
				if (stores.length === 0) storesError = 'No stores in your area.';
			} else {
				stores = [];
			}
		});
	});

	function locationToQuery(location:Partial<Location>) {
		const params: [string,string|number][] = [];
		for (const [k,v] of Object.entries(location)) {
			if (typeof v !== 'string' && typeof v !== 'number') continue;
			if (k === 'place_id') continue; // we don't need this
			params.push([k,v]);
		}
		if (vertical) params.push(['vertical', vertical]);
		qryParams.forEach(([k,v]) => params.push([k,v]));
		return `${onlyParams ? '' : '/'}?${params.map(([k,v]) => `${k}=${v}`).join('&')}`;
	}

	function jitEmit(e: MouseEvent & { currentTarget: EventTarget & HTMLAnchorElement}, type:'saved'|'recent'|'geo'|'store', location:Location) {
		if (navOnSelect) return;
		e.preventDefault();
		// console.log('intercepting click', e.currentTarget.href)
		dispatch('selected', { type, location });
	}

	onDestroy(() => {
		subStores?.unsubscribe();
	});
</script>

<div class="address">


	{#if !$showStoreSelectModal}
		<h2 class="display-s bold">
			{#if title}
				{title}
			{:else if vertical === Vertical.Store}
				Add your location to order from shops in your area
			{:else if vertical === Vertical.Grocery}
				Add your location to shop the world of Pick n Pay
			{:else}
				Add your location to order from your favourite restaurants
			{/if}
		</h2>
	
		<Search
			bind:value={$addr}
			placeholder="Enter delivery address"
			loading={!isSearchReady || gmBusy}
			classes={searchIsActive ? 'focused' : ''}
			onFocus={() => searchIsActive = true}
			onblur={() => searchIsActive = false}
			clear={() => addr.set('')}
		/>

		{#if !$geoOpted$}
		<button class="book-s medium flex-row align-center" on:click={() => setGeoOpted()} type="button">
			<span class="icon-current-location-20"></span>
			Use current locations
		</button>
		{/if}


		{#if $results}
			<ul class="search-results">
				{#each $results as item, i (item.place_id)}
					<li>
						<!-- this should be a button -->
						<a on:click|preventDefault={() => choosePlace(item.place_id)} href={`?place_id=${item.place_id}&${xp}`} on:keypress>
							<span class="check icon-check-empty-20"></span>
							<div>
								<span>{item.structured_formatting.main_text}</span>
								<span>{item.structured_formatting.secondary_text}</span>
							</div>
							<span class="icon-pin-20"></span>
						</a>
					</li>
				{/each}
				{#if !$results?.length}
					<div class="text-center">
						<Illustration src='general-error-banana-medium' />
						<li data-test-id="no-results"><em>No addresses found.</em></li>
					</div>
				{/if}
			</ul>
			<div>Powered by Google</div>
		{:else}
			{@const locationOpted = $geoOpted$}
		
			{#if $savedAddresses$}
				<h4 class="headline-s bold">Saved addresses</h4>
				<ul class="saved">
					{#each $savedAddresses$ as item, i}
					<li>
						<a href={(onlyParams ? '' : '/') + '?' + item.query + '&' + xp} id="linkLocationSaved_{item.label}" class:selected={selectedLabel === item.label} on:click={e => jitEmit(e,'saved', item)} data-sveltekit-noscroll>
							{#if selectedLabel === item.label}
								<span class="icon-check-on-20"></span>
							{:else}
								<span class="check icon-check-empty-20"></span>
							{/if}
							<div>
								<span>{getAddressLabel(item.label)}</span>
								<span>{xaddress(item).remove("province", "postal_code").toStr()}</span>
							</div>
							<span 
								class="icon {item.label === 'work' ? 'icon-briefcase-28' : 
									item.label === 'home' ? 'icon-kouch-28' : 'icon-home-28'}">
							</span>
						</a>
					</li>
					{/each}
				</ul>
			{/if}

			{#if $recent$}
				<h4 class="headline-s bold">Recent locations</h4>
				<ul class="recent">
					{#each $recent$ as item, index}
					<li>
						<a href={(onlyParams ? '' : '/') + '?' + item.query + '&' + xp} id="linkLocationRecent{index}" on:click={e => jitEmit(e, 'recent', item)} data-sveltekit-noscroll>
							<span class="icon-check-empty-20"></span>
							<div>
								<span>{xaddress(item).keep("street_number", "street_name").toStr()}</span>
								<span>{xaddress(item).keep("suburb", "town").toStr()}</span>
							</div>
							<span class="icon-history-28"></span>
						</a>
					</li>
					{/each}
				</ul>
			{/if}

			{#if locationOpted}
				{@const list = $geolocationAddresses$}
				{#if list?.length}
					<h4 class="headline-s bold">Current location addresses</h4>
					<ul class="geo-results">
						{#each list as item, index}
							<li>
								<a href="{locationToQuery(item.address) + '&' + xp}" id="linkLocationMaps{index}" on:click={e => jitEmit(e, 'geo', item.address)} data-sveltekit-noscroll>
									{removeCountryAndPostalCode(item.description)}<br>
								</a>
							</li>
						{/each}
					</ul>
				{:else}
					<!-- <div>We found no street addresses for your current location.</div> -->
				{/if}
			{/if}

			{#if !$profile$ && !$savedAddresses$}
				<hr>
				<!-- <h4 class="headline-s bold">Already have an account?</h4> -->
				<button class="button secondary" on:click={toggleSignIn}>Sign in for saved addresses</button>
			{/if}
			
		{/if}
	{/if}

	{#if $showStoreSelectModal}
		{#if vertical === Vertical.Grocery && stores && currentURL}
			{@const urlStore = currentURL.searchParams.get('store')}
			<!-- <h4 class="headline-s bold" id="storesHeading">Pick n Pay Store</h4> -->
			{#if storesError}
				<div>{JSON.stringify(storesError)}</div>
			{/if}
			{#each stores as store, index}
				{@const q = query(currentURL).set('store', store.id)}
				<div>
					<a href="{currentURL.pathname}?{q}&{xp}" data-testid={`store-option-${index}`} on:click={e => jitEmit(e, 'store', q.toObject())} data-sveltekit-noscroll>
						<StoreOption {store} selected={urlStore ? store.id === urlStore : store.default} />
					</a>
				</div>
			{/each}
		{/if}
	{/if}
</div>

<!-- {#if showLogin}
	<Modal on:close={() => showLogin = false} id="signin">
		<SignIn on:ok={() => showLogin = false}/>
	</Modal>
{/if} -->

<style lang="scss">
	@use 'color';
	h4#storesHeading {
		padding-top: 2.67rem;
		border-top: 2px solid color.$separator;
	}
</style>
